import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, getDataFrameSpecFromGetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import AnalysisTableSelector from 'analysis-tool/ui/components/AnalysisTableSelector';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assertAndContinue} from 'Asserts';
import DataFrameColumnRelationshipMultiSelector from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {COMBINE_TABLES_FINISHED_EVENT, COMBINE_TABLES_STARTED_EVENT} from 'performance/processAnalysesEvents';
import CombineTablesMethodSelector from 'pipelines/joins/CombineTablesMethodSelector';
import JoinTypeSelector from 'pipelines/joins/JoinTypeSelector';
import {PreviewStepParams, PreviewOutput} from 'pipelines/PipelineDAO';
import {FONT_PROXIMA_12, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import {StepperV2, stepperStyles} from 'ui-library/stepper/Stepper';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {getColumnMappingStepperStepParams} from '../column-mapping/ColumnMappingStepperStep';
import {
  AutoMapperColumnJoinType,
  AutoMapperMode,
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
} from '../column-mapping/ColumnMappingUtil';
import DataFrameColumnAutoMapper from '../column-mapping/DataFrameColumnAutoMapper';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import DataFrameNameField from '../steps/DataFrameNameField';
import CombineDataFramesPreview from './CombineDataFramesPreview';
import {
  CombineDataFramesState,
  combineDataFramesGlobalState,
  DEFAULT_COMBINE_DATA_FRAMES_STATE,
  getStepParams,
} from './CombineDataFramesState';

const previewAsyncGlobalState =
  makeUseGlobalClientState<
    AsyncState<
      PreviewOutput<PipelineStepType.JOIN>,
      [PreviewStepParams<PipelineStepType.JOIN> | PreviewStepParams<PipelineStepType.UNION_DATASETS>]
    >
  >(NOT_FETCHED_ASYNC_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

function PairedTableSelector({
  analysis,
  state,
  setState,
}: {
  analysis: AnalysisGraphQL;
  state: CombineDataFramesState;
  setState: (state: CombineDataFramesState) => void;
}): JSX.Element {
  const updateTables = ({
    leftTable,
    rightTable,
  }: Pick<CombineDataFramesState, 'leftTable' | 'rightTable'>): void => {
    setState({
      ...state,
      leftTable,
      rightTable,
      relationships:
        isEqual(leftTable, state.leftTable) && isEqual(rightTable, state.rightTable)
          ? state.relationships
          : [],
      dataFrameName:
        state.dataFrameNameHasUserEdits || leftTable == null || rightTable == null
          ? state.dataFrameName
          : `${leftTable.data.dataFrame.name} | ${rightTable.data.dataFrame.name}`,
    });
  };

  return (
    <div className={css(styles.tableSelectorWrapper)}>
      <div className={css(styles.tableSelector, styles.tableSelectorLeft)}>
        <AnalysisTableSelector
          analysis={analysis}
          includeViews={true}
          selectedLineageNode={state.leftTable}
          onSelect={(leftTable) => {
            updateTables({...state, leftTable});
          }}
          dataTestAttrs={{component: 'CombineDataFramesView', element: 'leftTableSelector'}}
        />
      </div>
      <div className={css(styles.tableSelector)}>
        <AnalysisTableSelector
          analysis={analysis}
          includeViews={true}
          selectedLineageNode={state.rightTable}
          onSelect={(rightTable) => {
            updateTables({...state, rightTable});
          }}
          dataTestAttrs={{component: 'CombineDataFramesView', element: 'rightTableSelector'}}
        />
      </div>
    </div>
  );
}

/**
 * @package
 */
export type CombineDataFramesViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.COMBINE_DATA_FRAMES>,
  | 'analysis'
  | 'clientStateId'
  | 'onCloseStepInitializationView'
  | 'onOpenStepInitializationView'
  | 'selectedItem'
>;

export default function CombineDataFramesView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
  selectedItem,
}: CombineDataFramesViewProps): JSX.Element {
  const [state, setState] = combineDataFramesGlobalState.useGlobalClientState(clientStateId);
  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);
  const [selectedUnionPrimaryColumns, setSelectedUnionPrimaryColumns] = useState<
    Array<DataFrameColumnDef | null>
  >([]);
  const [selectedUnionSecondaryColumns, setSelectedUnionSecondaryColumns] = useState<
    Array<DataFrameColumnDef | null>
  >([]);
  const [isUnionColumnsManuallyChanged, setIsUnionColumnsManuallyChanged] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);

  useEffect(() => {
    logProfilerTimingEvent(COMBINE_TABLES_STARTED_EVENT);
  }, []);

  function generatePreview(
    data: PreviewStepParams<PipelineStepType.JOIN>,
    setAsyncState: (nextState: AsyncState<PreviewOutput<PipelineStepType.JOIN>>) => void
  ): void {
    AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
  }

  const {previewStepParams, addStepParams} = getStepParams(
    state.combineMethod,
    state.joinType,
    state.leftTable,
    state.rightTable,
    state.relationships,
    selectedUnionPrimaryColumns,
    selectedUnionSecondaryColumns,
    state.dataFrameName
  );

  useAutoUpdatePreview(previewStepParams, generatePreview, (nextState) => {
    setPreviewState(nextState);
  });

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );

  const sameColumnsSelectedMoreThanOnceInUnion =
    new Set(selectedUnionPrimaryColumns).size !== selectedUnionPrimaryColumns.length ||
    new Set(selectedUnionSecondaryColumns).size !== selectedUnionSecondaryColumns.length;

  const previewDataFrame: DataFrame | null =
    getMostRecentData(previewState)?.dataFrames[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME][0] ?? null;

  const finalize = async (): Promise<void> => {
    try {
      if (addStepParams == null) {
        assertAndContinue(false, 'Tried to finalize combining data frames with incomplete form state.');
        return;
      }

      setIsFinalizing(true);
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        addStepParams,
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      logProfilerTimingEvent(COMBINE_TABLES_FINISHED_EVENT, {
        combinationType: 'join',
        joinType: state.joinType,
        error: null,
      });
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
    } catch (e) {
      logProfilerTimingEvent(COMBINE_TABLES_FINISHED_EVENT, {
        combinationType: 'join',
        joinType: state.joinType,
        error: e,
      });
      throw e;
    } finally {
      setIsFinalizing(false);
      setState(DEFAULT_COMBINE_DATA_FRAMES_STATE);
    }
  };

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={`${clientStateId}-combine-data-frames-view`}
      dataFrames={[]}
      renderAdditionalPreviewContent={() => (
        <CombineDataFramesPreview clientStateId={clientStateId} state={state} previewState={previewState} />
      )}
      disableFinalizeButton={
        addStepParams == null ||
        isFinalizing ||
        (state.combineMethod === PipelineStepType.UNION_DATASETS && sameColumnsSelectedMoreThanOnceInUnion)
      }
      isFinalizing={isFinalizing}
      messages={[]}
      onCancel={() => {
        setState(DEFAULT_COMBINE_DATA_FRAMES_STATE);
        onCloseStepInitializationView();
      }}
      onFinalize={finalize}
      title="Combine analysis tables"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            onOpenStepInitializationView({...newSelectedItem, stepIdToReplace: selectedItem.stepIdToReplace});
          }}
        />
      }
    >
      <StepperV2
        steps={[
          {
            title: 'Select a combination method',
            content: (
              <CombineTablesMethodSelector
                combineTablesMethod={state.combineMethod}
                onSelectCombineTablesMethod={(combineMethod) => {
                  setState({...state, combineMethod});
                }}
              />
            ),
            key: 'select-combine-method',
          },
          {
            title: 'Select tables to combine',
            content: <PairedTableSelector analysis={analysis} state={state} setState={setState} />,
            key: 'select-tables',
          },
          {
            title: 'Configure combine method',
            content: (
              <>
                {state.combineMethod === PipelineStepType.JOIN && (
                  <>
                    <div className={css(styles.subsectionTitle)}>Select a join type</div>
                    <JoinTypeSelector
                      joinType={state.joinType}
                      onSelectJoinType={(joinType) => {
                        setState({...state, joinType});
                      }}
                    />
                  </>
                )}
                {state.combineMethod === PipelineStepType.JOIN && state.joinType === 'POSITIONAL' && null}
                {state.combineMethod === PipelineStepType.JOIN && state.joinType !== 'POSITIONAL' && (
                  <>
                    <div className={css(styles.subsectionTitle)}>Specify join keys</div>
                    {state.leftTable && state.rightTable ? (
                      <DataFrameColumnRelationshipMultiSelector
                        addItemLabel="Add a join key"
                        leftDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(
                          state.leftTable.data.dataFrame
                        )}
                        rightDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(
                          state.rightTable.data.dataFrame
                        )}
                        relationships={state.relationships}
                        onSelectRelationships={(relationships) => {
                          setState({...state, relationships});
                        }}
                      />
                    ) : (
                      <InlineBanner
                        bannerType={InlineBannerType.WARNING}
                        text="Please select tables to combine first."
                      />
                    )}
                  </>
                )}
                {state.combineMethod === PipelineStepType.UNION_DATASETS && (
                  <>
                    <div className={css(styles.subsectionTitle)}>Map columns to union</div>
                    The union table will inherit column header names from Analysis table 1.
                    {state.leftTable && state.rightTable ? (
                      <>
                        {sameColumnsSelectedMoreThanOnceInUnion && (
                          <div className={css(styles.bannerWrapper)}>
                            <InlineBanner
                              bannerType={InlineBannerType.ERROR}
                              text="Each column can only be selected once"
                            />
                          </div>
                        )}
                        <div className={css(styles.autoMapperWrapper)}>
                          <DataFrameColumnAutoMapper
                            templateColumns={state.leftTable.data.dataFrame.allColumnsJson}
                            aligningColumns={state.rightTable.data.dataFrame.allColumnsJson}
                            setSelectedTemplateColumns={setSelectedUnionPrimaryColumns}
                            setSelectedAligningColumns={setSelectedUnionSecondaryColumns}
                            initialAutoMapperMode={AutoMapperMode.NAME}
                            initialAutoMapperColumnJoinType={AutoMapperColumnJoinType.KEEP_SHARED}
                            allowModeChange={true}
                            allowColumnJoinTypeChange={false}
                            autoMap={!isUnionColumnsManuallyChanged}
                          />
                        </div>
                        <DataFrameColumnRelationshipMultiSelector
                          addItemLabel="Add a column pair"
                          leftDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(
                            state.leftTable.data.dataFrame
                          )}
                          rightDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(
                            state.rightTable.data.dataFrame
                          )}
                          relationships={selectedUnionPrimaryColumns.map(
                            (selectedColumn, index): DataFrameColumnRelationship => ({
                              leftColumn: selectedColumn?.systemName ?? null,
                              rightColumn: selectedUnionSecondaryColumns[index]?.systemName ?? null,
                            })
                          )}
                          onSelectRelationships={(relationships) => {
                            setSelectedUnionPrimaryColumns(
                              relationships.map(
                                (relationship) =>
                                  state.leftTable?.data.dataFrame.allColumnsJson.find(
                                    (column) => column.systemName === relationship.leftColumn
                                  ) ?? null
                              )
                            );
                            setSelectedUnionSecondaryColumns(
                              relationships.map(
                                (relationship) =>
                                  state.rightTable?.data.dataFrame.allColumnsJson.find(
                                    (column) => column.systemName === relationship.rightColumn
                                  ) ?? null
                              )
                            );
                            setIsUnionColumnsManuallyChanged(true);
                          }}
                        />
                      </>
                    ) : (
                      <InlineBanner
                        bannerType={InlineBannerType.WARNING}
                        text="Please select tables to combine first."
                      />
                    )}
                  </>
                )}
              </>
            ),
            key: 'configure-combine-method',
          },
          selectedItem.stepIdToReplace != null &&
            getColumnMappingStepperStepParams({
              templateDataFrameColumns,
              newDataFrameColumns: previewDataFrame?.columns ?? null,
              newDataFrameState: previewState.status,
              selectedColumns,
              setSelectedColumns,
              isManuallyChanged,
              setIsManuallyChanged,
              key: 'column-mapping',
            }),
          {
            title: 'Name table',
            content: (
              <DataFrameNameField
                hasUserEdits={state.dataFrameNameHasUserEdits}
                name={state.dataFrameName}
                onNameChange={(dataFrameName) => {
                  setState({...state, dataFrameName, dataFrameNameHasUserEdits: true});
                }}
              />
            ),
            key: 'name-table',
          },
        ].map((step) => {
          if (step == null) {
            return null;
          }
          if (step === false) {
            return false;
          }
          return {
            ...step,
            title: <div className={css(stepperStyles.defaultStepTitle)}>{step.title}</div>,
            content: <div className={css(stepperStyles.defaultStepContent)}>{step.content}</div>,
          };
        })}
      />
    </StepConfigurationViewTemplate>
  );
}

export const styles = StyleSheet.create({
  autoMapperWrapper: {
    marginTop: 20,
    marginBottom: 20,
  },
  subsectionTitle: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '10px 0px',
    ':nth-child(1)': {
      marginTop: 0,
    },
  },
  tableSelector: {
    width: 'calc(50% - 12px)',
  },
  tableSelectorLeft: {
    marginRight: 24,
  },
  tableSelectorWrapper: {
    display: 'inline-flex',
    width: '100%',
  },
  bannerWrapper: {
    marginTop: 20,
  },
});
